/**
 * Formas de devolver el capital de un préstamo (independientes de cómo se
 * calcula el interés) y validación del plan de capital personalizado.
 *
 * En el interior de Guatemala los flujos de caja de los negocios no son
 * parejos: el agricultor cobra en la cosecha, el comerciante vende más en
 * temporada de feria o fin de año. Por eso, además de la cuota nivelada, el
 * motor ofrece formas de pago que siguen el flujo real del cliente.
 */
import { keyedEnum } from '../catalogs/enum.js';
import { BASIS_POINTS_PER_UNIT } from '../money/money.js';
/**
 * Formas de devolver el capital:
 * - `level_installment` (cuota nivelada / sistema francés): capital + interés
 *   iguales en cada período. Lo más fácil de recordar para el cliente.
 * - `constant_principal` (capital constante / sistema alemán): el mismo
 *   capital cada período e interés sobre saldo, así que las cuotas bajan con
 *   el tiempo. Paga menos interés total que la cuota nivelada.
 * - `interest_only_bullet` (capital al vencimiento): solo intereses cada
 *   período y todo el capital en la última cuota. Útil para capital de
 *   trabajo que rota en un ciclo conocido.
 * - `single_payment` (pago único): capital e intereses en una sola cuota al
 *   final del plazo. Típico del crédito agrícola a cosecha.
 * - `custom_principal` (plan personalizado / estacional): el asesor define
 *   qué porcentaje del capital se paga en cada período (0 % en los meses
 *   flojos, más en temporada alta); el interés se paga cada período.
 */
export const REPAYMENT_METHODS = [
    'level_installment',
    'constant_principal',
    'interest_only_bullet',
    'single_payment',
    'custom_principal',
];
/** Catálogo con clave de formas de pago: `REPAYMENT_METHOD.single_payment`. */
export const REPAYMENT_METHOD = keyedEnum(REPAYMENT_METHODS);
/** Forma de pago de un préstamo cuando no se indica otra. */
export const DEFAULT_REPAYMENT_METHOD = REPAYMENT_METHOD.level_installment;
/** Etiquetas cortas en español de cada forma de pago. */
export const REPAYMENT_METHOD_LABELS = {
    level_installment: 'Cuota nivelada',
    constant_principal: 'Capital constante (cuotas decrecientes)',
    interest_only_bullet: 'Intereses periódicos y capital al vencimiento',
    single_payment: 'Pago único al vencimiento',
    custom_principal: 'Plan personalizado (estacional)',
};
/** Explicación en lenguaje sencillo de cada forma de pago, para el asesor y el cliente. */
export const REPAYMENT_METHOD_DESCRIPTIONS = {
    level_installment: 'Paga lo mismo cada período: al inicio la cuota es casi todo interés y al final casi todo capital.',
    constant_principal: 'Paga el mismo capital cada período más el interés del saldo: la primera cuota es la más alta y bajan con el tiempo.',
    interest_only_bullet: 'Paga solo intereses cada período y devuelve todo el capital en la última cuota.',
    single_payment: 'No paga nada durante el plazo: capital e intereses se pagan juntos al vencimiento.',
    custom_principal: 'Usted decide cuánto capital paga en cada período (por ejemplo, más en la cosecha); los intereses se pagan cada período.',
};
/**
 * Formas de pago donde la gracia a capital no aplica: ya definen por sí
 * mismas cuándo se paga el capital.
 */
export const GRACELESS_REPAYMENT_METHODS = [
    REPAYMENT_METHOD.interest_only_bullet,
    REPAYMENT_METHOD.single_payment,
    REPAYMENT_METHOD.custom_principal,
];
/**
 * Formas de pago cuya cuota cambia de un período a otro. La capacidad de
 * pago se evalúa con la cuota más alta en lugar de la "cuota regular".
 */
export const VARIABLE_INSTALLMENT_METHODS = [
    REPAYMENT_METHOD.constant_principal,
    REPAYMENT_METHOD.interest_only_bullet,
    REPAYMENT_METHOD.single_payment,
    REPAYMENT_METHOD.custom_principal,
];
/** Indica si la forma de pago admite períodos de gracia a capital. */
export function allowsPrincipalGrace(method) {
    return !GRACELESS_REPAYMENT_METHODS.includes(method);
}
/** 100 % expresado en puntos básicos: la suma que debe alcanzar un plan personalizado. */
export const FULL_PRINCIPAL_SHARE = BASIS_POINTS_PER_UNIT;
/**
 * Problemas de un plan de capital personalizado para un plazo dado. Lista
 * vacía = plan válido. Los mensajes están escritos para el asesor.
 */
export function principalPlanViolations(plan, termPeriods) {
    if (!plan || plan.length === 0)
        return ['Indique qué porcentaje del capital se paga en cada cuota'];
    const violations = [];
    if (plan.length !== termPeriods) {
        violations.push(`El plan debe tener una entrada por cuota: ${String(termPeriods)} cuotas, ${String(plan.length)} entradas`);
    }
    if (plan.some((share) => !Number.isInteger(share) || share < 0)) {
        violations.push('Cada porcentaje del plan debe ser cero o positivo');
    }
    const total = plan.reduce((sum, share) => sum + share, 0);
    if (total !== FULL_PRINCIPAL_SHARE) {
        violations.push(`Los porcentajes del plan deben sumar 100 % (suman ${formatShare(total)})`);
    }
    const lastShare = plan[plan.length - 1] ?? 0;
    if (lastShare === 0) {
        violations.push('La última cuota debe incluir capital para cerrar el préstamo');
    }
    return violations;
}
/** Decimales al mostrar un porcentaje del plan (puntos básicos → 2 decimales). */
const SHARE_DISPLAY_DECIMALS = 2;
/** Factor de puntos básicos a porcentaje. */
const BASIS_POINTS_PER_PERCENT = 100;
/** Porcentaje legible de una participación en puntos básicos: 3350 → "33.5 %". */
function formatShare(share) {
    return `${String(Number((share / BASIS_POINTS_PER_PERCENT).toFixed(SHARE_DISPLAY_DECIMALS)))} %`;
}
/**
 * Reparte el 100 % en partes iguales entre `periods` cuotas (el residuo va a
 * la última). Punto de partida del editor de planes personalizados.
 */
export function evenPrincipalPlan(periods) {
    if (!Number.isInteger(periods) || periods <= 0)
        throw new RangeError('El plazo debe ser de al menos un período');
    const base = Math.floor(FULL_PRINCIPAL_SHARE / periods);
    const plan = Array.from({ length: periods }, () => base);
    plan[periods - 1] = (FULL_PRINCIPAL_SHARE - base * (periods - 1));
    return plan;
}
/**
 * Incoherencias entre la forma de pago y el resto de condiciones. Lista
 * vacía = coherente. Se usa en productos, solicitudes, comité y simulador.
 */
export function repaymentTermsViolations(terms) {
    const violations = [];
    if (terms.principalGracePeriods > 0 && !allowsPrincipalGrace(terms.repaymentMethod)) {
        violations.push(`La forma de pago "${REPAYMENT_METHOD_LABELS[terms.repaymentMethod]}" no admite gracia a capital`);
    }
    if (terms.repaymentMethod === REPAYMENT_METHOD.custom_principal) {
        violations.push(...principalPlanViolations(terms.principalPlan, terms.termPeriods));
    }
    else if (terms.principalPlan && terms.principalPlan.length > 0) {
        violations.push('El plan de capital personalizado solo aplica a la forma de pago "Plan personalizado"');
    }
    return violations;
}
//# sourceMappingURL=repayment.js.map